import _ from 'lodash';
import * as types from './types';

import { getRequest, postRequest } from '../../services/requestService';
import {
  showErrorToast,
  showSuccessToast,
} from '../../services/notificationsService';
import { PREDEFINED_VIEWS } from '../../containers/OffersPage/constants';
import { reportEvent } from '../../services/analyticsService';

/**
 * Fetch offers for current buyers
 */
export const getOffers = (params) => async (dispatch) => {
  const payload = {
    isFetchingOffers: false,
    offers: [],
  };
  dispatch({
    type: types.SET_IS_FETCHING_OFFERS,
    payload: { isFetchingOffers: true },
  });

  try {
    const response = await postRequest('/offer/get-offers', params);
    const { items, reason, total } = response.data;

    if (reason) {
      showErrorToast(reason);
    } else {
      payload.offers =
        items?.map((item) => {
          const offers =
            item.events?.filter((event) => event.kind === 'offer') || [];
          const lastOffer =
            offers.length > 0 ? offers[offers.length - 1] : null;
          return { ...item, lastOffer };
        }) || [];
      payload.total = total;
    }
  } catch (e) {
    showErrorToast(`Fetching offers failed: ${e.message}`);
  }

  dispatch({
    type: types.SET_OFFERS,
    payload,
  });
};

/**
 * Fetch single offer
 */
export const getOfferById = (offerId) => async (dispatch) => {
  const payload = {
    isFetchingOffers: false,
    offers: [],
  };
  dispatch({
    type: types.SET_IS_FETCHING_OFFERS,
    payload: { isFetchingOffers: true },
  });

  const response = await getRequest(`/offer/${offerId}`);
  payload.selectedOffer = response.data.offer;

  dispatch({
    type: types.SET_OFFERS,
    payload,
  });
};

/**
 * Export offers of current Buyer
 */
export const exportOffers = (fileName, params) => async (dispatch) => {
  const payload = {
    isOfferDownloading: false,
  };
  dispatch({
    type: types.SET_IS_DOWNLOAD_OFFERS,
    payload: { isOfferDownloading: true },
  });

  try {
    const response = await postRequest('/offer/export', params);
    const { reason } = response.data;

    if (reason) {
      showErrorToast(reason);
    } else {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      reportEvent('dashboard', 'download_csv_success');
    }
  } catch (e) {
    showErrorToast(`Downloading offers failed: ${e.message}`);
    reportEvent('dashboard', 'download_csv_error');
  }

  dispatch({
    type: types.SET_IS_DOWNLOAD_OFFERS,
    payload,
  });
};

export const updateOffer = (
  params,
  callback,
  skipOfferUpdate = false,
) => async (dispatch, getState) => {
  const payload = {
    isFetchingOffers: false,
    offers: [],
  };
  const { currentPage, pageSize, appliedFilters } = getState().OfferReducer;

  try {
    dispatch({
      type: types.SET_IS_FETCHING_OFFERS,
      payload: { isFetchingOffers: true },
    });
    const response = await postRequest('/offer/update', {
      ...params,
      page: currentPage,
      pageSize,
      appliedFilters,
    });
    const { reason, items, total } = response.data;
    if (reason) {
      showErrorToast('Submitting an offer failed!');
    } else {
      const propertyMatches =
        items?.map((item) => {
          const offers =
            item.events?.filter((event) => event.kind === 'offer') || [];
          const lastOffer =
            offers.length > 0 ? offers[offers.length - 1] : null;
          return { ...item, lastOffer };
        }) || [];
      payload.offers = propertyMatches;
      payload.total = total;
      if (!skipOfferUpdate) {
        payload.selectedOffer = response?.data?.updatedMatch;
      }
    }
    showSuccessToast('Success! We updated your offer.');
    if (callback) {
      callback();
    }
  } catch (error) {
    showErrorToast('Submitting an offer failed!');
  }

  dispatch({
    type: types.SET_OFFERS,
    payload,
  });
};

export function setSelectedOffer(selectedOffer) {
  return {
    type: types.SET_OFFER,
    payload: {
      selectedOffer: selectedOffer || undefined,
    },
  };
}

export function setSelectedOfferForContact(selectedOffer) {
  return {
    type: types.SET_SELECTED_OFFER_FOR_CONTACT,
    payload: {
      selectedOfferForContact: selectedOffer,
    },
  };
}

export const setCurrentPage = ({ page }) => (dispatch) => {
  dispatch({
    type: types.SET_CURRENT_PAGE,
    payload: { currentPage: page },
  });
};

export const setPageSize = ({ pageSize }) => (dispatch) => {
  dispatch({
    type: types.SET_PAGE_SIZE,
    payload: { pageSize },
  });
};

export const resetAppliedFilters = (shouldResetQuery = true) => (
  dispatch,
  getState,
) => {
  const previousAppliedFilters = getState().OfferReducer.appliedFilters;

  if (shouldResetQuery) {
    dispatch({
      type: types.SET_APPLIED_FILTERS,
      payload: { appliedFilters: {} },
    });
  } else {
    dispatch({
      type: types.SET_APPLIED_FILTERS,
      payload: { appliedFilters: { query: previousAppliedFilters.query } },
    });
  }
};

export const setAppliedFilters = (key, filter) => (dispatch, getState) => {
  const previousAppliedFilters = getState().OfferReducer.appliedFilters;

  dispatch({
    type: types.SET_APPLIED_FILTERS,
    payload: {
      currentPage: 0,
      appliedFilters: { ...previousAppliedFilters, [key]: filter },
    },
  });
};

export const deleteAppliedFilter = (key, setPredefinedView) => (
  dispatch,
  getState,
) => {
  const previousAppliedFilters = getState().OfferReducer.appliedFilters;
  const newFilters = _.omit(previousAppliedFilters, key);
  if (!Object.keys(newFilters).length && setPredefinedView) {
    setPredefinedView(PREDEFINED_VIEWS.ALL_DEALS);
  }

  dispatch({
    type: types.SET_APPLIED_FILTERS,
    payload: { appliedFilters: newFilters },
  });
};

/**
 * Fetch available counties and markets
 */
export const getCountiesAndMarkets = (params) => async (dispatch) => {
  const payload = {
    isFetchingCountiesAndMarkets: false,
    counties: [],
    markets: [],
  };
  dispatch({
    type: types.SET_IS_FETCHING_COUNTIES_AND_MARKETS,
    payload: { isFetchingCountiesAndMarkets: true },
  });

  try {
    const response = await postRequest(
      '/offer/get-counties-and-markets',
      params,
    );
    const { counties, markets } = response.data;
    payload.counties = counties;
    payload.markets = markets;
  } catch (error) {
    console.log(error);
  }

  dispatch({
    type: types.SET_COUNTIES_AND_MARKETS,
    payload,
  });
};

export const fetchPendingActions = () => async (dispatch) => {
  const payload = {};
  try {
    const res = await getRequest('/offer/count-pending-actions');
    payload.pendingDealSubmissionMatchesCounter =
      res.data.pendingDealSubmissionMatchesCounter;
    payload.pendingPreDealsCounter = res.data.pendingPreDealsCounter;
  } catch (e) {
    console.log(e);
  }

  dispatch({ type: types.SET_PENDING_ACTIONS, payload });
};
